import { useMemo } from "react";
import { EntityWithCount } from "../types/cms";
import { useVideosWithEntities } from "./useVideosWithEntities";

/**
 * Brands or studios with their video count (all statuses), for the CMS.
 * Shares the admin catalog cache with the videos view.
 */
export function useEntitiesWithCount(collectionName: "agencies" | "companies") {
  const { videos, companies, agencies, loading, error, refetch } =
    useVideosWithEntities("admin");

  const entities: EntityWithCount[] = useMemo(() => {
    const field = collectionName === "agencies" ? "agencyId" : "enterpriseId";
    const counts = new Map<string, number>();
    videos.forEach((v) => {
      const id = v[field];
      if (id) counts.set(id, (counts.get(id) || 0) + 1);
    });
    const list = collectionName === "agencies" ? agencies : companies;
    return list.map((e) => ({ ...e, videoCount: counts.get(e.id) || 0 }));
  }, [videos, companies, agencies, collectionName]);

  return { entities, loading, error, refetch };
}

/** Brands or studios without counts. */
export function useEntities(collectionName: "agencies" | "companies") {
  const { companies, agencies, loading, error } = useVideosWithEntities("admin");
  return {
    entities: collectionName === "agencies" ? agencies : companies,
    loading,
    error,
  };
}
